import { FAKE } from "./fake";
import { requestMockLocationPermission } from "./permissionPrompt";
import type { GeoPayload } from "./types";

/** 浏览器取定位的超时。比 axios 的 10s 短一截：定位卡住时先按「取不到」处理，别拖住点游戏那一下。 */
const GEO_TIMEOUT_MS = 8000;

type BrowserGeoResult =
  | {
      ok: true;
      payload: Pick<GeoPayload, "latitude" | "longitude" | "accuracy">;
    }
  | { ok: false; reason: "unsupported" | "denied" | "unavailable" };

/**
 * 向浏览器要一次定位。
 *
 * 【这一步是真的，不是假数据】合规文档里客户端是 Android 原生 PeabodySDK
 * （自己申请 ACCESS_FINE_LOCATION），H5 没有这东西 —— 这里直接调
 * navigator.geolocation，浏览器会自己弹系统权限框，就是需求里说的
 * 「登录页面弹出获取地理位置弹窗」。只有「服务端判定合不合规」那一步是假的
 * （见 api/geo.ts），因为那个真要后端。
 *
 * ⚠️ 只在安全上下文（https / localhost）里可用。用局域网 IP（http://192.168.x.x）
 * 打开时浏览器一律报 PERMISSION_DENIED，跟用户点没点禁止无关。
 *
 * 【临时】用户在弹窗里点「Enable Location」重试时，拿不到定位也放行：
 * 见下方 resolveUnavailable —— 由 FAKE.geoGrantOnRetry 控制，只作用于
 * fromUserRetry 那一次，自动检查不受影响。接真实合规前必须关掉。
 *
 * 三种拿不到的情况分开返回，调用方要据此区分「用户点了禁止」和「设备定位挂了」：
 * 前者提示去开权限，后者提示换个信号好的地方。
 */
export const requestBrowserGeolocation = (
  /** 是否来自用户在地理围栏弹窗里点按钮的那一次重试（只影响临时兜底，见文件末尾） */
  fromUserRetry = false
): Promise<BrowserGeoResult> => {
  // 【临时】用自绘的弹窗代替系统权限框，理由见 permissionPrompt.ts 顶部。
  // 走这条时完全不碰 navigator.geolocation，所以不会出现「自己弹一次、系统再弹一次」。
  if (FAKE.geoUseMockPermissionSheet) {
    return requestMockLocationPermission().then(granted =>
      granted
        ? { ok: true as const, payload: MOCK_COORDS }
        : { ok: false as const, reason: "denied" as const }
    );
  }

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve(resolveUnavailable("unsupported", fromUserRetry));
  }

  return new Promise<BrowserGeoResult>(resolve => {
    // getCurrentPosition 的回调在极端情况下（页面被 bfcache 挂起再恢复）可能一个都不来，
    // 自己再加一层兜底，保证 promise 一定落地，否则点游戏会一直转圈。
    let settled = false;
    const settle = (result: BrowserGeoResult) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const fallbackTimer = setTimeout(
      () => settle(resolveUnavailable("unavailable", fromUserRetry)),
      GEO_TIMEOUT_MS + 2000
    );

    navigator.geolocation.getCurrentPosition(
      position => {
        clearTimeout(fallbackTimer);
        settle({
          ok: true,
          payload: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          },
        });
      },
      error => {
        clearTimeout(fallbackTimer);
        settle(
          resolveUnavailable(
            error.code === error.PERMISSION_DENIED ? "denied" : "unavailable",
            fromUserRetry
          )
        );
      },
      {
        enableHighAccuracy: true,
        timeout: GEO_TIMEOUT_MS,
        // 不吃缓存位置：合规判定要的是「此刻人在哪」，翻出半小时前的坐标等于没校验
        maximumAge: 0,
      }
    );
  });
};

/**
 * 【临时，接真实合规时整段删掉】浏览器给不了定位时怎么办。
 *
 * 分两种情况，刻意不一样：
 * - 登录时 / 点游戏时的自动检查（fromUserRetry=false）→ 如实返回失败，
 *   由 gate.ts 判定为 denied 并弹地理围栏。**拦截保持有效。**
 * - 用户在弹窗里点「Enable Location」的那一次（fromUserRetry=true）→
 *   FAKE.geoGrantOnRetry 开着就当定位拿到了，放行。否则那个按钮在
 *   浏览器永久禁止 / 非安全上下文下永远点不出结果，流程卡死在弹窗上。
 */
const resolveUnavailable = (
  reason: "unsupported" | "denied" | "unavailable",
  fromUserRetry: boolean
): BrowserGeoResult => {
  if (!fromUserRetry || !FAKE.geoGrantOnRetry) return { ok: false, reason };
  // 留个痕：否则下次有人问「为什么拒绝定位点一下就过了」要从头翻一遍代码
  console.warn(
    `[compliance] 浏览器定位不可用（${reason}），但这是用户点 Enable Location 的重试，FAKE.geoGrantOnRetry 开着 → 按定位成功放行`
  );
  return { ok: true, payload: MOCK_COORDS };
};

/** 【临时】假坐标：洛杉矶市中心。合不合规由 api/geo.ts 按 FAKE.geoCompliant 决定。 */
const MOCK_COORDS = { latitude: 34.0522, longitude: -118.2437, accuracy: 20 };
